import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import * as dayjs from 'dayjs';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { finalize, map, mergeMap, tap } from 'rxjs/operators';
import getHolidayTypesByAirports from '../../../../helpers/get-holiday-types-by-airports';
import { EngineTabTypes } from '../../../search-engine/models/engine-tab-types.enum';
import { LoadingItem } from '../../../shared/interfaces/loading-item';
import { ResultOrderTypes } from '../../../shared/models/result-order-types.enum';
import { BookingService } from '../../../shared/services/booking.service';
import { HolidayPrefetchService } from '../../../shared/services/holiday-prefetch.service';
import { LoadingService } from '../../../shared/services/loading.service';
import { PostMessageService } from '../../../shared/services/post-message.service';
import { ProjectService } from '../../../shared/services/project.service';
import { SEOService } from '../../../shared/services/seo.service';
import { WidgetService } from "../../../shared/services/widget.service";
import { IPrefetchDestinationsParams } from '../../../shared/interfaces/iprefetch-destinations-params';
import { ALL_HOLIDAY_TYPES, tourHolidayTypeList } from './helpers/holiday-types';
import { HolidayCard } from './interfaces/holiday-card';
import { HolidaySearchParams } from './interfaces/holiday-search-params';
import { HolidaySearchFilterService } from './services/holiday-search-filter.service';
import { HolidaySearchService } from './services/holiday-search.service';
import {CmsService} from "../../../cms/services/cms.service";
import {TourOperators} from "../../../../enums/Tops.enum";

@Component({
  selector: 'app-holiday-search',
  templateUrl: './holiday-search.component.html',
  styleUrls: ['./holiday-search.component.scss']
})
export class HolidaySearchComponent implements OnInit, OnDestroy {

  openEngine;
  openFilter;

  public brand: any;

  public holidayList$: Observable<HolidayCard[]>;
  paramMapSubscription: Subscription;
  currentParams: HolidaySearchParams;
  engineTabType: EngineTabTypes;
  resultsString: string;
  resultsNumber: number;
  selectedOrderType: ResultOrderTypes;
  isB2B: boolean;
  resultOrderTypes = ResultOrderTypes;
  onlyProposte = false;
  guestAges : number[];

  @ViewChild('mainContent') mainContentRef;
  @ViewChild('holidaySearchBody') holidaySearchBodyRef: ElementRef;

  private parityRateHack: boolean = true;

  constructor(private _route: ActivatedRoute,
              private _holidaySearchService: HolidaySearchService,
              private _holidayPrefetchService: HolidayPrefetchService,
              private _holidaySearchFilterService: HolidaySearchFilterService,
              private _seoService: SEOService,
              private _widgetService: WidgetService,
              private _projectService: ProjectService,
              private _postMessageService: PostMessageService,
              private _bookingService: BookingService,
              private _cmsService: CmsService,
              private _loadingService: LoadingService) {
  }

  isWidget$: Observable<boolean>;

  ngOnInit(): void {

    this._cmsService.brandConfig$.subscribe(brand => {
      if (brand) this.brand = brand;
    });

    this.isWidget$ = this._widgetService.isWidget();
    this._postMessageService.isB2B().subscribe(isB2B => {
      this.isB2B = isB2B;
    });

    this._seoService.setTitle('Ricerca Vacanze');
    this.selectOrderType(ResultOrderTypes.RECOMMENDED);
    this.engineTabType = this._route.snapshot.data.type;
    this.onlyProposte = this._route.snapshot.queryParamMap.get('proposte') === 'true';

    this.paramMapSubscription = this._route.paramMap.pipe(
      map(e => {
        const onlyCustomHoliday = this.onlyProposte;
        const parsedDate = dayjs(e.get('departureDate'), 'DD-MM-YYYY');
        let dateFrom;
        let dateTo;
        const destinations = [];
        const airports = [];
        if (onlyCustomHoliday && !this.onlyProposte) {
          dateFrom = parsedDate.startOf('month').format('YYYY-MM-DD');
          dateTo = parsedDate.endOf('month').format('YYYY-MM-DD');
        } else {
          dateFrom = parsedDate.format('YYYY-MM-DD');
          dateTo = dateFrom;
        }
        const parsedDestinationId = parseInt(e.get('destinationId'), 10);
        const airportsIdList = e.get('airportId').split(',');
        if (!isNaN(parsedDestinationId)) {
          destinations.push(parsedDestinationId);
        }
        for (const value of airportsIdList) {
          const airportId = value === "null" || value === null ? null : parseInt(value, 10);
          if (!isNaN(airportId) && airportId !== null) {
            airports.push(airportId);
          }
        }

        //  Trae le età dei passeggeri dalla rotta. In assenza del parametro lo richiede al service.
        const ages = e.get('ages')?.split(',').map(age=>Number(age)) || this._bookingService.getAgesArray();
        this.guestAges = ages;

        return {
          dateFrom,
          dateTo,
          onlyCustomHoliday,
          onlyNetQuotes: this.isB2B,
          destinations,
          airports,
          ages
        };
      }),
      mergeMap(e => {
        if (e.onlyCustomHoliday && e.destinations.length === 0) {
          const prefetchParams: IPrefetchDestinationsParams = {
            onlyCustomHoliday: e.onlyCustomHoliday,
            onlyNetQuotes: e.onlyNetQuotes,
            ages: e.ages
          };
          if (this.engineTabType === EngineTabTypes.Tour && !this.onlyProposte) {
            prefetchParams.holidayTypeList = tourHolidayTypeList;
          }
          return forkJoin([
            of(e),
            this._holidayPrefetchService.prefetchDestinations(prefetchParams)
          ]).pipe(
            map(x => {
              x[0].destinations = x[1];
              return x[0];
            }),
            mergeMap(z => {
              if (z.onlyCustomHoliday && z.airports.length === 0) {
                return forkJoin([
                  of(z),
                  this._holidayPrefetchService.prefetchDeparturePlaces({
                  onlyCustomHoliday: z.onlyCustomHoliday,
                  onlyNetQuotes: z.onlyNetQuotes,
                  destination: z.destinations,
                  ages: z.ages
                })
                ]).pipe(
                  map(y => {
                    y[0].airports = y[1];
                    return y[0];
                  })
                );
              }
              return of(z);
            })
          );
        }
        if (!e.onlyCustomHoliday) {
          return forkJoin([
            of(e),
            this._projectService.hasOnlyNetQuotes()
          ]).pipe(
            map(x => {
              x[0].onlyNetQuotes = x[1];
              return x[0];
            })
          );
        }
        return of(e);
      })
    ).subscribe(e => {
      this._holidaySearchFilterService.pagination.currentPage = 1;
      this.currentParams = this.createSearchParams(e.dateFrom, e.dateTo, e.destinations, e.airports, e.onlyCustomHoliday, e.onlyNetQuotes, e.ages);
      this.holidayList$ = this.searchHolidays(this.currentParams);
    });
  }

  selectOrderType(orderType: ResultOrderTypes): void {
    switch (orderType) {
      case ResultOrderTypes.DATE:
        this.selectedOrderType = ResultOrderTypes.DATE;
        break;
      case ResultOrderTypes.PRICE:
        this.selectedOrderType = ResultOrderTypes.PRICE;
        break;
      case ResultOrderTypes.RECOMMENDED:
        this.selectedOrderType = ResultOrderTypes.RECOMMENDED;
        break;
    }
  }

  createSearchParams(dateFrom, dateTo, destination, airports, onlyCustomHoliday, onlyNetQuotes, ages): HolidaySearchParams {

    const objToSend: HolidaySearchParams = {
      date_from: dateFrom,
      date_to: dateTo,
      destinationList: destination,
      ages,
      onlyCustomHoliday,
      onlyNetQuotes,
      holidayTypeList: [],
      departurePlaces: airports,
      type: 5,
      excludeLiveResponse: true
    };
    if (!this.onlyProposte) {
      objToSend.holidayTypeList = getHolidayTypesByAirports(airports);
      if (this.engineTabType === EngineTabTypes.Tour) {
        objToSend.holidayTypeList = tourHolidayTypeList;
      }
    } else {
      objToSend.holidayTypeList = ALL_HOLIDAY_TYPES;
    }

    return objToSend;
  }

  searchHolidays(searchParams: HolidaySearchParams): Observable<any> {
    const loadingItem: LoadingItem = {id: 'holiday-search', message: 'Stiamo cercando le migliori vacanze per te...'};
    this._loadingService.addLoad(loadingItem);

    return this._holidaySearchService.getFormattedHolidays(searchParams).pipe(
      tap(e => setTimeout(() => {
        //#region HACK parity rate
        if (this.parityRateHack) {
          let customHolidays = e.filter(x => x.isCustom && !x.wsHoliday);
          for( let customHoliday of customHolidays) {
            let wsHoliday = e.find(x => x.accomodation?.id == customHoliday.accomodation?.parent?.Id);
            const isAlpi = TourOperators.alpiIDs.includes(Number(customHoliday?.tourOperator));
            const isOta = TourOperators.otaId === customHoliday?.tourOperator;
            // Se la struttura del prodotto custom non è presente nel WS => rimuovi
            if (!wsHoliday && isAlpi) {
              console.log("NO WS, isAlpi true", customHoliday);
              e.splice(e.indexOf(customHoliday), 1);
            // Se invece è presente ed è ALPI
            } else if (isAlpi) {
              console.log("ALPI WS HOLIDAY", wsHoliday);
              wsHoliday.isCustom = true;
              wsHoliday.wsHoliday = true;
              wsHoliday.wsRealTourOperatorId = customHoliday.realTourOperatorId;
              e.splice(e.indexOf(customHoliday), 1);
            } else if(isOta && wsHoliday) {
              e.splice(e.indexOf(wsHoliday),1);
            }
          }
        }
        //#endregion HACK parity rate
        this.resultsNumber = e.length;
        this.resultsString = `(${e.length} Risulati)`;
        this._holidaySearchFilterService.updateTrigger.next(Math.random());
      })),
      finalize(() => {
        this._loadingService.removeLoad(loadingItem);
      })
    );
  }

  onPageChange($event): void {
    this.mainContentRef?.nativeElement?.scrollIntoView();
  }

  onApplyFilter($event: boolean): void {
    if ($event) {
      this.scrollOnTop();
    }
  }

  scrollOnTop(): void {
    this.holidaySearchBodyRef.nativeElement.scrollIntoView();
  }

  ngOnDestroy(): void {
    this.paramMapSubscription?.unsubscribe();
  }

}
